/**
 * Controls availability of real-time screen collaboration.
 *
 * Reads window._env_.COLLAB_EDITING_ENABLED (emitted by env.sh at container start; see
 * designer/env.sh and public/env-config.js).
 *
 * OPT-IN semantics: collaboration is OFF unless the deployment explicitly sets
 * COLLAB_EDITING_ENABLED=true (or "1"). Absent, empty and unrecognized values all mean
 * off. Fail-closed because enabling collab switches the screen tree onto the op-stream
 * persistence path (whole-tree PUTs are refused with 409) — a deployment provisioned
 * without the variable must not land there unnoticed. env.sh omits a variable it cannot
 * read, so "absent" is the normal shape of "nobody decided".
 * The backend evaluates the SAME env var name (Backend CollabFeature) — both sides must
 * agree, and with the flag off the designer's behavior is byte-identical to the legacy
 * lock model.
 */
export function isCollabEditingEnabled(): boolean {
  const raw: string | undefined = window._env_?.COLLAB_EDITING_ENABLED;
  const normalized: string = raw?.trim().toLowerCase() ?? "";
  return normalized === "true" || normalized === "1";
}

/**
 * Workflow (BPMN) collab sub-gate — mirrors the backend CollabWorkflowFeature semantics
 * exactly: never broader than the parent flag, and COLLAB_WORKFLOW_ENABLED absent means
 * "inherit the parent's value". Only an explicit "false"/"0" turns workflow collab off
 * while screen collab stays on (fallback to the legacy per-workflow advisory lock).
 */
export function isWorkflowCollabEnabled(): boolean {
  if (!isCollabEditingEnabled()) return false;
  const raw: string | undefined = window._env_?.COLLAB_WORKFLOW_ENABLED;
  if (raw === undefined || raw === null) return true;
  const normalized: string = raw.trim().toLowerCase();
  return !(normalized === "false" || normalized === "0");
}
